import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { SecureContent } from '../utils/sanitize';
import { getImageUrlForStyle, getImageUrl } from '../utils/imageUtils';

const DirectorMessagePage = () => {
  const [directorData, setDirectorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDirectorData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getDirectorMessage();
        setDirectorData(response.data);
      } catch (error) {
        setError('Failed to load director message');
        console.error('Error fetching director message:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDirectorData();
  }, []);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-crimson mb-4"></i>
          <p className="text-gray-600">Loading Director's Message...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <div className="relative bg-slate py-20">
        {directorData?.bannerImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: getImageUrlForStyle(directorData.bannerImage) }}
          ></div>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-sans font-bold text-white mb-4">
            {directorData?.bannerTitle || "Director's Message"}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {directorData?.bannerSubtitle || 'Academic leadership for transformative education and research'}
          </p>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          {/* Main Content Card */}
          <div className="bg-white rounded-xl shadow-soft p-12 md:p-16">
            <div className="grid md:grid-cols-4 gap-12 mb-8">
              {/* Photo */}
              <div className="md:col-span-1">
                <div className="bg-gray-100 rounded-lg aspect-[3/4] flex items-center justify-center overflow-hidden">
                  {directorData?.directorImage ? (
                    <img 
                      src={getImageUrl(directorData.directorImage)} 
                      alt={directorData.directorName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <i className="fas fa-user-graduate text-8xl text-gray-400"></i>
                  )}
                </div>
                <div className="mt-6 text-center">
                  <h3 className="font-bold text-xl text-slate">
                    {directorData?.directorName || 'Dr. Anita Desai'}
                  </h3>
                  <p className="text-base text-gray-500 mt-2">
                    {directorData?.directorTitle || 'Director'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {directorData?.directorInstitution || 'FiT Engineering College'}
                  </p>
                </div>
              </div>
            
              {/* Message Content */}
              <div className="md:col-span-3 space-y-6">
                <SecureContent 
                  content={directorData?.messageContent || 
                    `<p>Dear Students and Colleagues,</p>
                     <p>As Director of FiT Engineering College, I am honored to lead our academic mission...</p>`
                  }
                  className="text-gray-600 leading-relaxed rich-text-content"
                />
                <p className="text-gray-600 leading-relaxed font-semibold mt-8">
                  With warm regards,<br/>
                  <span className="text-crimson text-xl">{directorData?.directorName || 'Dr. Anita Desai'}</span><br/>
                  <span className="text-base font-normal">
                    {directorData?.directorTitle || 'Director'}, {directorData?.directorInstitution || 'FiT Engineering College'}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectorMessagePage;